import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  FormHelperText,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Rule as RuleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Block as BlockIcon,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { suppressionListsAPI } from '../../../services/leadGenAPI';
import { api } from '../../../services/authService';
import { useSelector } from 'react-redux';

const CreateSuppressionList = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'contact', // 'contact', 'company', 'geographic', 'demographic'
    list_type: 'blacklist', // 'blacklist', 'whitelist'
    scope: 'global', // 'global', 'campaign_specific'
    status: 'active',
    account_id: '', // Required field
    rules: [],
  });

  // Account selection state
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [newRule, setNewRule] = useState({
    field: '',
    operator: '',
    value: '',
  });

  // Fetch accounts on component mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await api.get('/crm/accounts/');
      setAccounts(response.data);
      // Set the first account as default if available
      if (response.data && response.data.length > 0) {
        setFormData(prev => ({ ...prev, account_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fieldOptions = {
    contact: [
      { value: 'email', label: 'Email Address' },
      { value: 'phone', label: 'Phone Number' },
      { value: 'first_name', label: 'First Name' },
      { value: 'last_name', label: 'Last Name' },
      { value: 'company_domain', label: 'Company Domain' },
    ],
    company: [
      { value: 'company_name', label: 'Company Name' },
      { value: 'company_domain', label: 'Company Domain' },
      { value: 'industry', label: 'Industry' },
      { value: 'company_size', label: 'Company Size' },
    ],
    geographic: [
      { value: 'country', label: 'Country' },
      { value: 'state', label: 'State/Province' },
      { value: 'city', label: 'City' },
      { value: 'zipcode', label: 'ZIP/Postal Code' },
    ],
    demographic: [
      { value: 'age', label: 'Age' },
      { value: 'gender', label: 'Gender' },
      { value: 'income_level', label: 'Income Level' },
      { value: 'education', label: 'Education Level' },
    ],
  };

  const operatorOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Not Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
    { value: 'in', label: 'In' },
    { value: 'not_in', label: 'Not In' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'List name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.account_id) {
      newErrors.account_id = 'Account is required';
    }

    if (formData.rules.length === 0) {
      newErrors.rules = 'At least one rule is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const suppressionListData = {
        name: formData.name,
        description: formData.description,
        suppression_type: formData.type,
        list_type: formData.list_type,
        is_global: formData.scope === 'global',
        status: formData.status,
        account_id: formData.account_id,
        created_by_id: currentUser?.id,
        initial_rules: formData.rules.map(rule => ({
          rule_type: rule.field,
          field_name: rule.field,
          operator: rule.operator,
          value: rule.value,
          is_active: true,
          priority: 0
        })),
      };

      await suppressionListsAPI.createList(suppressionListData);
      navigate('/lead-gen/suppression');
    } catch (error) {
      console.error('Error creating suppression list:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addRule = () => {
    // Check if required fields are filled
    const isValueRequired = newRule.operator !== 'is_empty' && newRule.operator !== 'is_not_empty';
    const isValid = newRule.field && newRule.operator && (!isValueRequired || newRule.value);
    
    if (isValid) {
      setFormData(prev => ({
        ...prev,
        rules: [...prev.rules, { ...newRule, id: Date.now() }]
      }));
      setNewRule({ field: '', operator: '', value: '' });
      if (errors.rules) {
        setErrors(prev => ({ ...prev, rules: '' }));
      }
    }
  };

  const removeRule = (ruleId) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.filter(rule => rule.id !== ruleId)
    }));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'contact': return <PersonIcon />;
      case 'company': return <BusinessIcon />;
      case 'geographic': return <LocationIcon />;
      case 'demographic': return <PersonIcon />;
      default: return <BlockIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'contact': return 'primary';
      case 'company': return 'secondary';
      case 'geographic': return 'info';
      case 'demographic': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/lead-gen/suppression')}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" color="text.primary">
            Create Suppression List
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create List'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Basic Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="List Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    error={!!errors.description}
                    helperText={errors.description}
                    multiline
                    rows={3}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={(e) => handleInputChange('type', e.target.value)}
                    >
                      <MenuItem value="contact">Contact</MenuItem>
                      <MenuItem value="company">Company</MenuItem>
                      <MenuItem value="geographic">Geographic</MenuItem>
                      <MenuItem value="demographic">Demographic</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>List Type</InputLabel>
                    <Select
                      value={formData.list_type}
                      label="List Type"
                      onChange={(e) => handleInputChange('list_type', e.target.value)}
                    >
                      <MenuItem value="blacklist">
                        <Box display="flex" alignItems="center" gap={1}>
                          <BlockIcon color="error" />
                          Blacklist (Exclude matching leads)
                        </Box>
                      </MenuItem>
                      <MenuItem value="whitelist">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircle color="success" />
                          Whitelist (Only allow matching leads)
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.account_id}>
                    <InputLabel>Account *</InputLabel>
                    <Select
                      value={formData.account_id}
                      label="Account *"
                      onChange={(e) => handleInputChange('account_id', e.target.value)}
                      disabled={accountsLoading}
                    >
                      {accounts.map(account => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.account_id && (
                      <FormHelperText>{errors.account_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={formData.scope}
                      label="Scope"
                      onChange={(e) => handleInputChange('scope', e.target.value)}
                    >
                      <MenuItem value="global">Global</MenuItem>
                      <MenuItem value="campaign_specific">Campaign Specific</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status === 'active'}
                        onChange={(e) => handleInputChange('status', e.target.checked ? 'active' : 'inactive')}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Rules Configuration */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Suppression Rules
              </Typography>
              
              {errors.rules && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.rules}
                </Alert>
              )}

              {/* Add New Rule */}
              <Box sx={{ mb: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add New Rule
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={newRule.field}
                        label="Field"
                        onChange={(e) => setNewRule(prev => ({ ...prev, field: e.target.value }))}
                      >
                        {fieldOptions[formData.type]?.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={newRule.operator}
                        label="Operator"
                        onChange={(e) => setNewRule(prev => ({ ...prev, operator: e.target.value }))}
                      >
                        {operatorOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Value"
                      value={newRule.value}
                      onChange={(e) => setNewRule(prev => ({ ...prev, value: e.target.value }))}
                      disabled={newRule.operator === 'is_empty' || newRule.operator === 'is_not_empty'}
                      placeholder={newRule.operator === 'is_empty' || newRule.operator === 'is_not_empty' ? 'Not required for this operator' : 'Enter value...'}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={addRule}
                      disabled={(() => {
                        const isValueRequired = newRule.operator !== 'is_empty' && newRule.operator !== 'is_not_empty';
                        return !newRule.field || !newRule.operator || (isValueRequired && !newRule.value);
                      })()}
                      fullWidth
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Existing Rules */}
              {formData.rules.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Current Rules ({formData.rules.length})
                  </Typography>
                  <List>
                    {formData.rules.map((rule, index) => (
                      <ListItem
                        key={rule.id}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: '#fafafa',
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={() => removeRule(rule.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <RuleIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={(() => {
                            const isValueOperator = rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty';
                            return isValueOperator 
                              ? `${rule.field} ${rule.operator} "${rule.value}"`
                              : `${rule.field} ${rule.operator}`;
                          })()}
                          secondary={`Rule ${index + 1}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Preview
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  List Details
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getTypeIcon(formData.type)}
                  <Chip 
                    label={formData.type} 
                    size="small" 
                    color={getTypeColor(formData.type)}
                    variant="outlined"
                  />
                  <Chip 
                    label={formData.list_type === 'whitelist' ? 'Whitelist' : 'Blacklist'} 
                    size="small" 
                    color={formData.list_type === 'whitelist' ? 'success' : 'error'}
                    variant="outlined"
                    icon={formData.list_type === 'whitelist' ? <CheckCircle /> : <BlockIcon />}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  <strong>Name:</strong> {formData.name || 'Not specified'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>List Type:</strong> {formData.list_type === 'whitelist' ? 'Whitelist (Only allow matching leads)' : 'Blacklist (Exclude matching leads)'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Account:</strong> {accounts.find(acc => acc.id === formData.account_id)?.name || 'Not selected'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Scope:</strong> {formData.scope.replace('_', ' ')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Status:</strong> {formData.status}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Rules:</strong> {formData.rules.length}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formData.description || 'No description provided'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateSuppressionList; 